import { useCallback, useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { effectiveProductSearchQuery } from '../../shared/product-search/productSearchUx';
import { type ProductSearchSuggestionItem } from '../../shared/product-search/components/ProductSearchSuggestionsPanel';
import { formatStockQuantity } from '../application/inventoryUtils';
import type { AddStockModalProps } from './addStockModal.types';

type UseAddStockModalParams = Pick<
  AddStockModalProps,
  | 'visible'
  | 'entryReason'
  | 'entrySupplierId'
  | 'productQueryDebounced'
  | 'productResults'
  | 'productLoadingMore'
  | 'productHasMore'
  | 'stocks'
  | 'selectedProduct'
  | 'suppliers'
  | 'onSelectProduct'
  | 'onLoadMoreProducts'
>;

export const useAddStockModal = ({
  entrySupplierId,
  productQueryDebounced,
  productResults,
  productLoadingMore,
  productHasMore,
  stocks,
  selectedProduct,
  suppliers,
  onSelectProduct,
  onLoadMoreProducts,
}: UseAddStockModalParams) => {
  const scrollRef = useRef<ScrollView>(null);
  const scrollContentRef = useRef<View>(null);

  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);

  const supplierLabel = useMemo(() => {
    if (!entrySupplierId) return 'Sin proveedor';
    return suppliers.find((s) => s.id === entrySupplierId)?.name ?? 'Proveedor';
  }, [entrySupplierId, suppliers]);

  const suggestionsOpen =
    !selectedProduct && effectiveProductSearchQuery(productQueryDebounced).length > 0;

  const suggestionItems = useMemo<ProductSearchSuggestionItem[]>(
    () =>
      productResults.map((product) => {
        const branchStock = stocks.find((row) => row.productId === product.id);
        const stockHint = branchStock
          ? `actual ${formatStockQuantity(branchStock.quantity, product.unitOfMeasure)}`
          : 'sin stock en esta sucursal';
        const sku = product.sku ?? '—';
        const supplierHint = product.preferredSupplierName
          ? ` · ${product.preferredSupplierName}`
          : '';
        return {
          id: product.id,
          title: product.name,
          subtitle: `${sku} · ${stockHint}${supplierHint}`,
          imageUrl: product.imageUrl ?? null,
        };
      }),
    [productResults, stocks]
  );

  const handlePickSuggestion = useCallback(
    (item: ProductSearchSuggestionItem) => {
      const product = productResults.find((p) => p.id === item.id);
      if (product) onSelectProduct(product);
    },
    [onSelectProduct, productResults]
  );

  return {
    scrollRef,
    scrollContentRef,
    suppliers,
    supplierPickerOpen,
    setSupplierPickerOpen,
    supplierLabel,
    suggestionsOpen,
    suggestionItems,
    handlePickSuggestion,
    productHasMore,
    productLoadingMore,
    onLoadMoreProducts,
  };
};
